/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import queryString from "query-string";

import ColorStack from "app/components/colorsStack/ColorStack";
import type { GraphType } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import {
    EGraphType,
    GRAPH_TYPES,
    webSourceToDeviceMap,
} from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import type { BasicDurations } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import type { ValueOf } from "pro-features/types";
import type { ChartType } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { addPartialDataZones } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { granularities } from "pro-features/utils";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { IPageFilters } from "../../pages/serp-players/FiltersSection/Types";
import { EGraphGranularities } from "app/common/keyword-research/serp-players/consts";

import type { ExtraFiledType, LegendDataPoint } from "./ChartLabels";
import type {
    ITransformedDataPoint,
    GraphData,
} from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import {
    transformGraphResponse,
    transformSiteData,
    DEFAULT_COLOR,
    sumSeriesYAtIndex,
} from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";

const fetchService = DefaultFetchService.getInstance();
const getTotalClicksKey = () => translate("keywordanalysis.organic2.clicks.over.time.total.clicks");
const getOtherClicksKey = () => translate("keywordanalysis.organic2.clicks.over.time.other.clicks");

export const graphTypeToExtraFiledType: Record<EGraphType, ExtraFiledType> = {
    [EGraphType.overview]: "isTotal",
    // @ts-ignore
    [EGraphType["chart-stacked-share"]]: "isOther",
    [EGraphType["stacked-chart"]]: undefined,
};

export const useGraphData = (
    selectedGranularity: EGraphGranularities,
    selectedGraphDuration: ValueOf<typeof BasicDurations>,
    filters: Partial<IPageFilters>,
    endpoint: string,
    graphEndpoint?: string,
) => {
    const { params } = useCurrentStateAndParams();
    const { page = 1 } = filters;
    const colorStack = useRef(new ColorStack(CHART_COLORS.chartMainColors));
    const [transformedData, setTransformedData] = useState<ITransformedDataPoint[]>([]);
    const [percentageData, setPercentageData] = useState<ITransformedDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const graphTotalData = useRef<Record<string, number>>(null);
    const [isInPercentageMode, setIsInPercentageMode] = useState<boolean>(false);
    const pageNumber = useRef<number>(1);
    const durationObj = DurationService.getDurationData(params.duration);
    const isLast28 = params.duration === "28d";

    const hasPartialWeek = () => {
        const hasMonthlySelectedOnSnapshot = selectedGranularity === EGraphGranularities.MONTHLY && !isLast28;
        const isSunday = durationObj.raw.from.day() === 0;
        const hasDailySelected = selectedGranularity === EGraphGranularities.DAILY;
        return !isSunday && !hasDailySelected && !hasMonthlySelectedOnSnapshot;
    };

    const getGraphParams = () => {
        const { from, to, isWindow } = durationObj.forAPI;
        const deviceType = webSourceToDeviceMap[params.webSource];
        const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
        const keywordGroupForApi = isKeywordGroup ? params.keyword.slice(1) : params.keyword;
        const { activationSets, ...restFilters } = filters;
        return {
            timeGranularity: granularities[selectedGranularity],
            From: from,
            To: to,
            keys: keywordGroupForApi,
            country: params.country,
            Device: deviceType,
            Duration: selectedGraphDuration,
            isWindow,
            ...restFilters,
        };
    };
    const getSelectedSites = () => {
        const filteredList = transformedData.filter(({ isOther, isTotal }) => !isOther && !isTotal);
        const selectedItems = filteredList.filter(({ visible }) => visible);
        return { SitesFilterExact: selectedItems.map((item) => item.name).join(",") };
    };
    const excelLink = `${graphEndpoint}?${queryString.stringify({ ...getGraphParams(), ...getSelectedSites() })}`;

    const checkIfAllZero = (data): boolean => {
        const allZero = Object.values(data).every((site) => Object.values(site).every((value) => value === 0));

        return allZero;
    };

    const requestGraphData = async () => {
        try {
            const shouldResetCheckedLegends = page !== pageNumber.current;
            const GraphDataResponse = await fetchService.get<
                Record<"SitesClicks" | "Totals", GraphData | GraphData[string]>
            >(endpoint, getGraphParams());
            let graphData = GraphDataResponse.SitesClicks as GraphData;
            if (checkIfAllZero(graphData)) {
                graphData = {};
            }
            graphTotalData.current = GraphDataResponse.Totals as GraphData[string];

            const graphDataTransformed = transformGraphResponse(
                graphData,
                colorStack.current,
                shouldResetCheckedLegends ? [] : transformedData,
                hasPartialWeek(),
            );

            if (shouldResetCheckedLegends) {
                pageNumber.current = page;
            }
            return { graphData: graphDataTransformed };
        } catch (err) {
            setError(`Failed to fetch graph data: ${err.message}`);
            throw err;
        }
    };

    const calculateTotals = (
        graphData: ITransformedDataPoint[],
        isTotalVisible = !isInPercentageMode,
        isOthersVisible = isInPercentageMode,
    ) => {
        const total = transformSiteData(
            getTotalClicksKey(),
            graphTotalData.current,
            true,
            false,
            isTotalVisible,
            hasPartialWeek(),
        );
        const selectedItemsData = graphData.filter(({ visible }) => visible);
        const othersTrend = Object.entries(graphTotalData.current).reduce((agr, [date, totalDataPoint], idx) => {
            agr[date] = totalDataPoint - sumSeriesYAtIndex(selectedItemsData, idx);
            return agr;
        }, {});
        const others = transformSiteData(
            getOtherClicksKey(),
            othersTrend,
            false,
            true,
            isOthersVisible,
            hasPartialWeek(),
        );

        return { total, others };
    };

    const calculatePercentageTrend = (data: ITransformedDataPoint[]): ITransformedDataPoint[] => {
        const totalItem = data.find(({ isTotal }) => isTotal);
        return data.map((siteData) => ({
            ...siteData,
            data: siteData.data.map((item, idx) => ({
                ...item,
                y: item.y === 0 ? 0 : item.y / totalItem.data[idx].y,
            })),
        }));
    };

    /**
     * Redistributes percentages among visible legends when Others is hidden,
     * scaling them proportionally so they sum to 100%.
     */
    const redistributePercentagesWhenOthersRemoved = (data: ITransformedDataPoint[]): ITransformedDataPoint[] => {
        const othersItem = data.find((item) => item.isOther);
        const visibleItems = data.filter((item) => item.visible && !item.isOther && !item.isTotal);

        if (!othersItem || othersItem.visible || visibleItems.length === 0) {
            return data;
        }

        // Pre-calculate sums for each time index once to avoid O(n²) complexity
        const sumsByIndex =
            visibleItems[0]?.data?.map((_, idx) => visibleItems.reduce((sum, item) => sum + item.data?.[idx]?.y, 0)) ??
            [];

        return data?.map((siteData) => {
            if (siteData.isOther || siteData.isTotal || !siteData.visible) {
                return siteData;
            }

            return {
                ...siteData,
                data: siteData?.data?.map((item, idx) => {
                    const sumOfVisiblePercentages = sumsByIndex[idx] ?? 0;

                    if (sumOfVisiblePercentages === 0) {
                        return item;
                    }

                    return {
                        ...item,
                        y: item.y / sumOfVisiblePercentages,
                    };
                }),
            };
        });
    };

    const updateData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            colorStack.current.reset();
            const { graphData } = await requestGraphData();

            const isPercentage = GRAPH_TYPES[params.graphType] === "chart-stacked-share";
            isPercentage && setIsInPercentageMode(true);
            const graphDataWithZones = addPartialDataZones(graphData, {
                chartType: chartTypes.AREA,
            });
            const { total, others } = calculateTotals(graphDataWithZones, !isPercentage, isPercentage);
            const totalWithZones = addPartialDataZones([total], {
                chartType: chartTypes.AREA,
            })[0];
            const othersWithZones = addPartialDataZones([others], {
                chartType: chartTypes.AREA,
            })[0];
            total.averageData && graphDataWithZones.push(totalWithZones);
            others.averageData && graphDataWithZones.push(othersWithZones);

            const newPercentageData = calculatePercentageTrend(graphDataWithZones);

            setTransformedData(graphDataWithZones);
            setPercentageData(newPercentageData);
        } catch (e) {}
        setIsLoading(false);
    };

    useEffect(() => {
        updateData();
    }, [selectedGranularity, selectedGraphDuration, params]);

    const handleSiteToggle = (series: LegendDataPoint) => {
        const chosenIndex = transformedData.findIndex((item) => item.name === series?.name);
        const selectedItem = transformedData[chosenIndex];

        // release and acquire color
        let acquiredColor: string = DEFAULT_COLOR;
        if (selectedItem && !selectedItem.isTotal && !selectedItem.isOther) {
            if (selectedItem.visible) {
                colorStack.current.release(selectedItem.color);
            } else {
                acquiredColor = colorStack.current.acquire();
            }
        }
        const modifiedData = [
            ...transformedData.slice(0, chosenIndex),
            {
                ...transformedData[chosenIndex],
                visible: !transformedData[chosenIndex].visible,
                color: acquiredColor,
            },
            ...transformedData.slice(chosenIndex + 1),
        ];

        const othersItem = modifiedData.find((item) => item.isOther);
        const isOthersHidden = othersItem?.visible === false;

        const applyPercentageRedistribution = (data: ITransformedDataPoint[]) => {
            return isInPercentageMode && isOthersHidden ? redistributePercentagesWhenOthersRemoved(data) : data;
        };

        if (!selectedItem.isTotal && !selectedItem.isOther) {
            let totalItem;
            let othersItemInFiltered;
            const filteredList = modifiedData.filter((item: ITransformedDataPoint) => {
                const { isOther, isTotal } = item;
                if (isOther) {
                    othersItemInFiltered = item;
                }
                if (isTotal) {
                    totalItem = item;
                }
                return !isOther && !isTotal;
            });
            const { total, others } = calculateTotals(filteredList, totalItem?.visible, othersItemInFiltered?.visible);
            filteredList.push(total, others);
            const filteredListWithZones = addPartialDataZones(filteredList, {
                chartType: chartTypes.AREA,
            });
            const percentageDataCalculated = calculatePercentageTrend(filteredListWithZones);

            setPercentageData(applyPercentageRedistribution(percentageDataCalculated));
            setTransformedData(filteredListWithZones);
        } else {
            const percentageDataCalculated = calculatePercentageTrend(modifiedData);

            setPercentageData(applyPercentageRedistribution(percentageDataCalculated));
            setTransformedData(modifiedData);
        }
    };

    const handleGraphTypeChange = (chartType: ChartType) => {
        const filteredList = transformedData.filter(({ isOther, isTotal }) => !isOther && !isTotal);
        const filteredListWithZones = addPartialDataZones(filteredList, { chartType });
        const { total, others } = calculateTotals(filteredListWithZones, !isInPercentageMode, isInPercentageMode);

        const totalWithZones = addPartialDataZones([total], { chartType })[0];
        const othersWithZones = addPartialDataZones([others], { chartType })[0];
        filteredListWithZones.push(totalWithZones, othersWithZones);
        setPercentageData(calculatePercentageTrend(filteredListWithZones));
        setTransformedData(filteredListWithZones);
    };

    const onGraphTypeChange = (graphType: GraphType) => {
        switch (graphType) {
            case "chart-stacked-share":
                setIsInPercentageMode(true);
                return handleGraphTypeChange(chartTypes.AREA);
            default:
                setIsInPercentageMode(false);
                return handleGraphTypeChange(graphType === "overview" ? chartTypes.LINE : chartTypes.AREA);
        }
    };
    return {
        transformedData,
        percentageData,
        isLoading,
        handleSiteToggle,
        handleGraphTypeChange: onGraphTypeChange,
        excelLink,
        error,
    };
};
